import { createTranslator, type Locale } from 'next-intl'

import { parse, TYPE, type MessageFormatElement } from '@formatjs/icu-messageformat-parser'

import type { ResolvedTranslations } from './types'
import type { C2CTemplateVariables } from './variables'

/**
 * WAPI `formatICUMessageWithBusinessLogic` parity: a TOP-LEVEL select argument
 * whose options include an `Empty` branch renders that branch when its value is
 * missing/empty ('' / null / undefined — 0 and false are real values), instead
 * of the whole key dropping out. Returns the extra values to merge in.
 */
function emptyBranchDefaults(
    template: string,
    values: Record<string, string | number>,
): Record<string, string> {
    const extra: Record<string, string> = {}
    let elements: MessageFormatElement[]
    try {
        elements = parse(template, { ignoreTag: true })
    } catch {
        return extra
    }
    for (const el of elements) {
        if (
            el.type === TYPE.select &&
            'Empty' in el.options &&
            (values[el.value] === undefined || values[el.value] === '')
        ) {
            extra[el.value] = 'Empty'
        }
    }
    return extra
}

/**
 * Render resolved ICU templates against a variable bag for a locale.
 *
 * Uses next-intl's ICU engine. A key whose template references a variable that is
 * missing/undefined is **omitted** rather than rendered broken — this reproduces
 * WAPI's `filterEmptyValues` behavior (content with absent data simply doesn't
 * render). Keys that render cleanly are returned as plain strings.
 */
export function renderTemplates(
    templates: ResolvedTranslations,
    variables: C2CTemplateVariables,
    locale: string,
): Record<string, string> {
    const translate = createTranslator({
        // store/loader handle all store locales as strings; the caller passes an app
        // locale here, narrowed to next-intl's declared Locale union.
        locale: locale as Locale,
        messages: templates,
        // rethrow so a missing-variable / malformed template surfaces per-key below
        onError: (error) => {
            throw error
        },
    })
    // Coerce to next-intl's accepted value shape; drop null/undefined so a template
    // referencing an absent variable throws → the key is omitted (WAPI parity).
    const values: Record<string, string | number> = {}
    for (const [name, value] of Object.entries(variables)) {
        if (value === null || value === undefined) {
            continue
        }
        values[name] = typeof value === 'boolean' ? String(value) : value
    }
    const out: Record<string, string> = {}
    for (const key of Object.keys(templates)) {
        try {
            out[key] = translate(key, {
                ...values,
                ...emptyBranchDefaults(templates[key], values),
            })
        } catch {
            // omit: a referenced variable was absent (WAPI filterEmptyValues parity)
        }
    }
    return out
}

// Per-ENTITY content keys, excluded from the route render because they interpolate
// variables that only exist in a per-carrier / per-review context — this carrier's
// averageDuration / minDuration / weekendTrips / stationName, or an individual review's
// fields. The route render runs ONCE with route-level variables and has no single carrier
// or review in scope, so rendering these here would starve them (missing variable → the key
// drops) or fill them with the wrong route-level value. They render on dedicated per-entity
// paths instead: carrierCard* via renderCarrierCard (once per carrier, with that carrier's
// bag); the review keys aren't rendered in nexus at all (reviews arrive as structured
// facts.carrierReviews data and render via React components, not ICU templates).
//
// This mirrors WAPI's City2CityController exactly: its getContent iterates only the keys
// filtered `!isCarrierKey && !isCarrierReviewKey && !isIndividualReviewKey`, and routes the
// carrier/review keys to separate getCarrierContent / getCarrierReviewContent functions.
// The three prefixes here are WAPI's isCarrierKey/isCarrierReviewKey/isIndividualReviewKey.
const PER_CARRIER_KEY = /^(carrierCard|carrierReviewCard|individualReviewCard)/
// The route render's default key filter: everything EXCEPT the per-carrier keys.
const routeKeyFilter = (key: string): boolean => !PER_CARRIER_KEY.test(key)

// Whitespace-before-punctuation fixups (empty ICU plural branches leave "{name} ."):
//  - ROUTE content collapses to a bare comma — WAPI's own quirk, kept for byte-fidelity
//    with the live route pages nexus mirrors.
//  - CARRIER content tightens naturally (drop the space, keep the mark): WAPI leaves carrier
//    cards' " ." uncollapsed, and "," there is an ugly comma splice ("Bay 2, In Baltimore"),
//    so nexus serves the correct "Bay 2. In Baltimore". See known-difference
//    `carrier-empty-plural-punctuation`.
const collapseToComma = (s: string): string => s.replace(/\s+[,.]/g, ',')
export const tightenPunctuation = (s: string): string => s.replace(/\s+([,.])/g, '$1')

/** WAPI's KEYS_THAT_MUST_BE_UPPERCASED — first character uppercased post-render. */
const UPPERCASED_KEYS = new Set(['popularStationsArrivalText', 'popularStationsDepartureText'])

/**
 * The WAPI `getContent` two-pass render for route-level copy. Digit-suffixed
 * SUB-templates (`titleWithStates1`, `dailyBusTicketsAverageText2`, …) render
 * first, in sorted order, and each rendered value is fed back into the variable
 * bag — so main templates interpolate them (`{titleWithStates1}`) and a later
 * sub-key can reference an earlier one. Both passes keep `renderTemplates`'
 * omit-on-missing-variable semantics, so a starved sub-key cascades into its
 * dependents dropping out (filterEmptyValues parity). Main keys also reproduce
 * WAPI's post-render fixups: the popular-stations text keys are first-letter
 * uppercased, and whitespace before `,`/`.` collapses to a bare comma —
 * an inherited WAPI quirk kept for byte-fidelity with the pages it serves.
 *
 * COMPOSE-THEN-SELECT: WAPI builds composed keys from DEFAULT definitions
 * (getContent) and only afterwards swaps selected variants into the standalone
 * keys (substituteVariantContent renders variants against a bag of default
 * sub-renders) — so a `{subKey}` reference always sees the default text, never
 * a variant. `feedTemplates` carries the default-resolved templates for that
 * feed; it defaults to `templates` (identical when nothing is variant-pooled,
 * e.g. section renders and leaf routes).
 */
export function renderTemplatesComposed(
    templates: ResolvedTranslations,
    variables: C2CTemplateVariables,
    locale: string,
    feedTemplates: ResolvedTranslations = templates,
    keyFilter: (key: string) => boolean = routeKeyFilter,
    punctFixup: (s: string) => string = collapseToComma,
): Record<string, string> {
    const keys = Object.keys(templates).filter(keyFilter)
    const subKeys = keys.filter((k) => /\d$/.test(k)).sort()
    const mainKeys = keys.filter((k) => !/\d$/.test(k)).sort()

    const out: Record<string, string> = {}
    const bag: C2CTemplateVariables = { ...variables }
    for (const key of subKeys) {
        const feedTemplate = feedTemplates[key] ?? templates[key]
        const fed = renderTemplates({ [key]: feedTemplate }, bag, locale)[key]
        if (fed !== undefined) {
            bag[key] = fed
        }
        // standalone value: the (possibly variant-selected) template, rendered
        // against the default-fed bag — exactly substituteVariantContent's order
        const rendered =
            templates[key] === feedTemplate
                ? fed
                : renderTemplates({ [key]: templates[key] }, bag, locale)[key]
        if (rendered !== undefined) {
            out[key] = rendered
        }
    }
    for (const key of mainKeys) {
        let rendered = renderTemplates({ [key]: templates[key] }, bag, locale)[key]
        if (rendered === undefined) {
            continue
        }
        if (UPPERCASED_KEYS.has(key) && rendered.length > 0) {
            rendered = rendered[0].toUpperCase() + rendered.slice(1)
        }
        out[key] = punctFixup(rendered)
    }
    return out
}

/**
 * The per-carrier counterpart to `renderTemplatesComposed`: renders the carrier-card
 * keys `renderTemplatesComposed` deliberately excludes (see PER_CARRIER_KEY), against a
 * bag of THIS carrier's stats. Same two-pass composite — `carrierCardText1/2/3` render and
 * feed `carrierCardText` — and the same missing-variable omit. The whitespace-before-punct
 * fixup here TIGHTENS naturally (" ." → ".") rather than the route's collapse-to-comma:
 * WAPI leaves carrier cards' raw " ." (from empty ICU plural branches) uncollapsed and a
 * comma there would be a splice, so nexus serves the clean sentence break. See
 * known-difference `carrier-empty-plural-punctuation`.
 *
 * `templates` should carry the route-level carrier-card templates (`carrierCardText1/2/3`,
 * `carrierCardLabel`) with any bespoke per-carrier `carrierCardText` (carrier/<code>.json)
 * already layered on top — the caller merges the store section over the route defaults.
 * Price placeholders (`__averagePriceUsd__` …) stay untouched for the render's
 * `buildCarrierAboutHtml` to fill from the carrier's metrics.
 */
export function renderCarrierCard(
    templates: ResolvedTranslations,
    variables: C2CTemplateVariables,
    locale: string,
): Record<string, string> {
    return renderTemplatesComposed(
        templates,
        variables,
        locale,
        templates,
        (key) => key.startsWith('carrierCard'),
        tightenPunctuation,
    )
}
